import { BRAND_COLORS, DEFAULT_WIDGET_GRADIENT } from '@/config/brandColors'

export type BackgroundFillMode = 'color' | 'gradient'
export type GradientType = 'linear' | 'radial'

export interface BackgroundFillValues {
  mode: BackgroundFillMode
  color: string
  gradientType: GradientType
  gradientAngle: number
  gradientColor1: string
  gradientColor2: string
  gradientColor1Location: number
  gradientColor2Location: number
}

export const DEFAULT_BACKGROUND_FILL: BackgroundFillValues = {
  mode: 'color',
  color: '#ffffff',
  gradientType: 'linear',
  gradientAngle: 135,
  gradientColor1: DEFAULT_WIDGET_GRADIENT.color1,
  gradientColor2: DEFAULT_WIDGET_GRADIENT.color2,
  gradientColor1Location: 0,
  gradientColor2Location: 100,
}

export function clampGradientLocation(value: unknown, fallback = 0): number {
  const n = Number(value)
  if (!Number.isFinite(n)) return fallback
  return Math.min(100, Math.max(0, Math.round(n)))
}

export function buildGradientCss(
  type: GradientType = 'linear',
  color1 = DEFAULT_WIDGET_GRADIENT.color1,
  color2 = DEFAULT_WIDGET_GRADIENT.color2,
  angle = 135,
  location1 = 0,
  location2 = 100,
): string {
  const loc1 = clampGradientLocation(location1, 0)
  const loc2 = clampGradientLocation(location2, 100)
  const stop1 = `${color1} ${loc1}%`
  const stop2 = `${color2} ${loc2}%`

  if (type === 'radial') {
    return `radial-gradient(circle, ${stop1}, ${stop2})`
  }

  return `linear-gradient(${angle}deg, ${stop1}, ${stop2})`
}

export function buildBackgroundFillCss(values: Partial<BackgroundFillValues>): string {
  const mode = values.mode || 'color'
  if (mode === 'gradient') {
    return buildGradientCss(
      values.gradientType || 'linear',
      values.gradientColor1 || DEFAULT_WIDGET_GRADIENT.color1,
      values.gradientColor2 || DEFAULT_WIDGET_GRADIENT.color2,
      Number(values.gradientAngle) || 135,
      values.gradientColor1Location ?? 0,
      values.gradientColor2Location ?? 100,
    )
  }

  return values.color || '#ffffff'
}

export function applyBackgroundFillStyle(
  styles: Record<string, string>,
  values: Partial<BackgroundFillValues>,
): Record<string, string> {
  const mode = values.mode || 'color'

  if (mode === 'gradient') {
    styles.background = buildBackgroundFillCss(values)
    delete styles.backgroundColor
    return styles
  }

  styles.backgroundColor = values.color || 'transparent'
  delete styles.background
  return styles
}

/** Background styles for widget roots / wrappers (solid or gradient). */
export function resolveWidgetBackgroundStyle(widget: {
  backgroundFillMode?: BackgroundFillMode
  backgroundColor?: string
  gradientType?: GradientType
  gradientAngle?: number
  gradientColor1?: string
  gradientColor2?: string
  gradientColor1Location?: number
  gradientColor2Location?: number
}): Record<string, string> {
  if (widget.backgroundFillMode === 'gradient') {
    return applyBackgroundFillStyle({}, {
      mode: 'gradient',
      gradientType: widget.gradientType,
      gradientAngle: widget.gradientAngle,
      gradientColor1: widget.gradientColor1,
      gradientColor2: widget.gradientColor2,
      gradientColor1Location: widget.gradientColor1Location,
      gradientColor2Location: widget.gradientColor2Location,
    })
  }

  if (widget.backgroundColor && widget.backgroundColor !== 'transparent') {
    return { backgroundColor: widget.backgroundColor }
  }

  return { backgroundColor: 'transparent' }
}

/** Map ExtraSection snake_case fields to BackgroundFillValues */
export function backgroundFillFromExtraSection(section: {
  background_type?: string
  background_color?: string
  gradient_type?: GradientType
  gradient_angle?: number
  gradient_color1?: string
  gradient_color2?: string
  gradient_color1_location?: number
  gradient_color2_location?: number
}): BackgroundFillValues {
  const mode: BackgroundFillMode = section.background_type === 'gradient' ? 'gradient' : 'color'

  return {
    mode,
    color: section.background_color || '#ffffff',
    gradientType: section.gradient_type || 'linear',
    gradientAngle: Number(section.gradient_angle) || 135,
    gradientColor1: section.gradient_color1 || DEFAULT_WIDGET_GRADIENT.color1,
    gradientColor2: section.gradient_color2 || DEFAULT_WIDGET_GRADIENT.color2,
    gradientColor1Location: clampGradientLocation(section.gradient_color1_location, 0),
    gradientColor2Location: clampGradientLocation(section.gradient_color2_location, 100),
  }
}

/** Map BaseWidget camelCase fields to BackgroundFillValues */
export function backgroundFillFromWidget(widget: {
  backgroundFillMode?: BackgroundFillMode
  backgroundColor?: string
  gradientType?: GradientType
  gradientAngle?: number
  gradientColor1?: string
  gradientColor2?: string
  gradientColor1Location?: number
  gradientColor2Location?: number
}): BackgroundFillValues {
  const mode: BackgroundFillMode =
    widget.backgroundFillMode === 'gradient' ? 'gradient' : 'color'

  return {
    mode,
    color:
      widget.backgroundColor !== undefined && widget.backgroundColor !== ''
        ? widget.backgroundColor
        : 'transparent',
    gradientType: widget.gradientType || 'linear',
    gradientAngle: Number(widget.gradientAngle) || 135,
    gradientColor1: widget.gradientColor1 || DEFAULT_WIDGET_GRADIENT.color1,
    gradientColor2: widget.gradientColor2 || DEFAULT_WIDGET_GRADIENT.color2,
    gradientColor1Location: clampGradientLocation(widget.gradientColor1Location, 0),
    gradientColor2Location: clampGradientLocation(widget.gradientColor2Location, 100),
  }
}
